import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import GuideComparisonTable from './GuideComparisonTable';
import usePageMeta from '../hooks/usePageMeta';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import { trackEvent } from '../analytics';
import { MAX_SPEED_MIN, MAX_SPEED_MAX } from '../lib/motionPace';
import './AdaptivePacingPage.css';

// "What is adaptive pacing?" — the definitional pillar page (#546).
//
// WHY A DEFINITION PAGE AT ALL. Almost nobody searches the phrase today, and
// that is the whole point: whoever publishes the page that defines a term gets
// cited as its definition, by Google and by the answer engines (ChatGPT,
// Perplexity) that quote a source rather than rank ten. So this page is written
// to BE the definition — one plain sentence high up, then the comparison table
// that a citation actually quotes — rather than to sell the product. The
// product appears once the technique has been explained, as the automatic
// implementation of it, and links to /motionpace (#544).
//
// AUDIENCE: contractors and DIY builders, not film people. No "temporal
// resampling", no "speed curve authoring" — every sentence has to survive being
// read on a phone in a truck.
//
// GATED AT BUILD TIME, exactly like /motionpace. This component knows nothing
// about that: the route is only declared when REACT_APP_MOTIONPACE_MARKETING is
// the string "true" (see App.js and scripts/prerender.routes.js), so with the
// flag dark the module is never even added to the webpack graph. Nothing here
// may read a runtime flag, because the page must render identically for a
// crawler with no JavaScript and no session.
//
// NAMING RULES ARE PRODUCT DECISIONS, NOT STYLE (#544/#546), and
// AdaptivePacingPage.test.js is the regression net for each of them:
//   - The brand is MotionPace. The technique is "adaptive pacing", and a video
//     made with it is "an adaptively paced timelapse" — never the noun stack
//     "adaptive pacing timelapse", and never a coined category name.
//   - "Nothing cut out", never "every frame kept": a paced render drops frames
//     to build the output, it just does not delete any stretch of the shoot.
//   - The speed ceiling is read from the engine, never typed in.
//   - The optional speed dial's default state is still undecided, so the page
//     may not claim it is on or off out of the box.

// The Max speed range, READ FROM THE MODULE THAT CLAMPS IT. lib/motionPace.js
// holds the mirrors of the server-side clamps in shared/motion_pace.py, so a
// number derived from them cannot outlive the limit it describes — the honesty
// contract /motionpace and the door pages already follow.
const MAX_SPEED_RANGE = { min: String(MAX_SPEED_MIN), max: String(MAX_SPEED_MAX) };
const MAX_SPEED_RANGE_LABEL = `${MAX_SPEED_RANGE.min}× to ${MAX_SPEED_RANGE.max}×`;

const CANONICAL_PATH = '/guides/what-is-adaptive-pacing';
const PAGE_TITLE = 'What Is Adaptive Pacing? A Plain-English Definition | Tweedia Studio';
const PAGE_DESCRIPTION =
  'Adaptive pacing is a timelapse whose playback speed follows the action — slower while ' +
  'something is happening, faster through the still stretches, with nothing cut out. How it ' +
  'differs from a constant-rate timelapse, a hyperlapse, and hand-made speed ramps.';

// THE definition. One sentence, and the only place it is written: the visible
// page, the meta description, the DefinedTerm markup and the FAQ all quote this
// constant, so the page cannot end up defining the term two different ways.
const DEFINITION =
  'Adaptive pacing is a timelapse whose playback speed follows the action: it slows down while ' +
  'something is happening and speeds up through the still stretches, with nothing cut out.';

const TOC = [
  { id: 'pacing_definition', label: 'The short answer' },
  { id: 'pacing_problem', label: 'The problem it solves' },
  { id: 'pacing_compare', label: 'Adaptive pacing vs the alternatives' },
  { id: 'pacing_how', label: 'How the speed gets decided' },
  { id: 'pacing_motionpace', label: 'MotionPace: adaptive pacing, done for you' },
  { id: 'pacing_when', label: 'When it helps — and when it does not' },
  { id: 'pacing_faq', label: 'FAQ' },
];

// Derived rather than hand-listed: a section added to the contents must also be
// reported to analytics, and two lists would drift.
const SECTION_IDS = TOC.map((item) => item.id);

// The comparison the page exists for. Term-defining citations quote a table far
// more readily than they quote prose, so the four techniques are set out on the
// same four axes — what picks the speed, what happens to the still stretches,
// what it costs you, and who it is for.
//
// The headers live beside the data because GuideComparisonTable writes each one
// onto its own cell as `data-label`, which is what the phone layout prints above
// the value. A column renamed here is renamed in both layouts at once.
const COMPARISON_COLUMNS = [
  { key: 'label', header: 'Technique' },
  { key: 'driver', header: 'What sets the speed' },
  { key: 'quiet', header: 'The still stretches' },
  { key: 'effort', header: 'Work it takes' },
  { key: 'bestFor', header: 'Best for' },
];

// No row here carries a `to`: this IS the adaptive pacing page, so its own row
// has nowhere to link, and a self-link is the thing #544's FAQ was pulled up
// for. Teal in these tables means "link", so every label on this page is white
// and the highlighted row is marked by its background instead.
const COMPARISON = [
  {
    id: 'adaptive',
    label: 'Adaptive pacing',
    highlight: true,
    driver: 'The footage — how much moved between each pair of frames',
    quiet: 'Speed up automatically, to a ceiling you set',
    effort: 'One switch',
    bestFor: 'Long fixed-camera shoots where work comes in bursts',
  },
  {
    id: 'constant',
    label: 'Constant-rate timelapse',
    driver: 'One speed you pick up front; it never changes',
    quiet: 'Get as much screen time as the busy ones',
    effort: 'Pick an interval, stitch the frames',
    bestFor: 'Short shoots, or steadily changing subjects',
  },
  {
    id: 'hyperlapse',
    label: 'Hyperlapse',
    driver: 'One speed you pick, plus you move the camera',
    quiet: 'Get the same screen time — the speed never varies',
    effort: 'High: reposition and line up every shot',
    bestFor: 'Traveling shots where the camera move is the show',
  },
  {
    id: 'ramps',
    label: 'Manual speed ramps',
    driver: 'You, by hand, one keyframe at a time',
    quiet: 'Whatever you set, wherever you set it',
    effort: 'Hours in a video editor, per clip',
    bestFor: 'Short hero clips you want to control second by second',
  },
];

// One plain-English paragraph per technique, for readers who bounced off the
// table. Keyed to the same ids so the two can be checked against each other.
const COMPARISON_NOTES = [
  {
    id: 'constant',
    title: 'Constant-rate timelapse — the normal kind',
    body:
      'This is what almost every timelapse is. You shoot a frame every 10 minutes, play them ' +
      'back at 30 a second, and the whole shoot runs at one fixed speed from beginning to end. ' +
      'It is simple and it is predictable, and that is exactly the problem: an empty yard on a ' +
      'rained-out Tuesday gets the same slice of your video as the morning the crane showed up.',
  },
  {
    id: 'hyperlapse',
    title: 'Hyperlapse — a moving constant-rate timelapse',
    body:
      'A hyperlapse is a timelapse where the camera travels between shots, so the finished clip ' +
      'sweeps through a space instead of staring at one. People often reach for the word when ' +
      'they mean "fast", but the speed is still constant: what makes it a hyperlapse is that the ' +
      'camera moved, not that the pace changed. The two ideas are unrelated — a hyperlapse can ' +
      'be adaptively paced, and usually is not.',
  },
  {
    id: 'ramps',
    title: 'Manual speed ramps — the hand-made version',
    body:
      'A speed ramp is you telling a video editor to run this bit at 2× and that bit at 8×, with ' +
      'a smooth blend between them. Editors have done it for decades, and on a 20-second clip it ' +
      'looks great. On a six-month build it is unusable: you would have to watch the whole raw ' +
      'shoot to know where the interesting parts are, then place every ramp yourself, and redo ' +
      'the lot if you re-cut anything.',
  },
  {
    id: 'adaptive',
    title: 'Adaptive pacing — the footage decides',
    body:
      'Adaptive pacing takes the same idea and takes your hands off it. Software reads the shoot, ' +
      'works out where things were actually moving, and sets the speed moment by moment to match ' +
      '— hundreds of changes in a single clip, none of which you placed. You never watch the raw ' +
      'footage, and nothing is cut out: the quiet stretches are still there, they just go past ' +
      'quickly.',
  },
];

const WHEN_IT_HELPS = [
  'A camera bolted in one place for weeks or months — construction, a remodel, a barn raise.',
  'Any shoot where the interesting part is a small slice of the recording: a garage build, a ' +
    'deck, a garden through a season.',
  'Long stretches of nothing you still want on the record — overnight, weekends, a rained-out ' +
    'week — that you would rather not delete.',
  'Footage you have already shot. The pacing is an edit decision, so it works on the card you ' +
    'filled last winter.',
];

const WHEN_IT_DOES_NOT = [
  'A subject that changes at a steady rate the whole way through — a sunset, a tide, a flower ' +
    'opening. There is no quiet stretch to speed through, so a constant rate is already right.',
  'A clip that is short enough to watch in full. If the raw shoot is under a minute, pacing it ' +
    'is solving a problem you do not have.',
  'A shot where the camera is moving. That is a hyperlapse question, not a pacing one — though ' +
    'you can pace one if you want to.',
];

// THE single source for the FAQ: it renders the visible section AND feeds the
// FAQPage markup, so the two can never disagree. A rich-text answer would force
// a second hand-kept plain-text copy for the structured data, which is exactly
// how those drift apart.
const FAQ = [
  {
    q: 'What is adaptive pacing?',
    a: DEFINITION,
  },
  {
    q: 'What do you call a timelapse made this way?',
    a:
      'An adaptively paced timelapse, or a timelapse with adaptive pacing. It is a normal ' +
      'timelapse file — an MP4 you can post anywhere — and nothing about playing it is special.',
  },
  {
    q: 'Is adaptive pacing the same as a hyperlapse?',
    a:
      'No. A hyperlapse is about the camera moving between frames, and it still plays at one ' +
      'constant speed. Adaptive pacing leaves the camera where it is and varies the speed ' +
      'instead, to follow what is in the frame.',
  },
  {
    q: 'Does adaptive pacing cut footage out?',
    a:
      'No — nothing is cut out. The whole shoot is still in the video from first frame to last; ' +
      'the quiet stretches simply go past faster. Cutting the still stretches away entirely is a ' +
      'different edit, usually called trimming.',
  },
  {
    q: 'How much faster do the quiet parts play?',
    a:
      `That is your call. In Tweedia Studio you set a ceiling anywhere from ${MAX_SPEED_RANGE_LABEL} ` +
      'the base speed, and the busy stretches settle back to the base speed no matter where the ' +
      'ceiling sits. A low ceiling reads as a gentle drift; a high one flies.',
  },
  {
    q: 'Do I need a special camera for it?',
    a:
      'No. Adaptive pacing happens in the edit, not in the camera, so any footage works — a ' +
      'jobsite camera, a GoPro, a phone on a windowsill, or a folder of stills from a DSLR.',
  },
  {
    q: 'Can I do it by hand instead?',
    a:
      'Yes, with speed ramps in any video editor, and for a short clip that is a perfectly good ' +
      'answer. It stops being practical at length, because you have to watch the raw shoot to ' +
      'find the interesting parts before you can place a single ramp.',
  },
];

function buildJsonLd() {
  const url = `https://tweediastudio.com${CANONICAL_PATH}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: 'What Is Adaptive Pacing?',
        description: PAGE_DESCRIPTION,
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Tweedia Studio', url: 'https://tweediastudio.com' },
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
        image: 'https://tweediastudio.com/tweedia_tour_poster.jpg',
      },
      // The node this page exists to publish. DefinedTerm is how a page says "I
      // am the definition of this phrase" in a form an answer engine can lift
      // whole, which is the entire strategy behind defining the term first.
      {
        '@type': 'DefinedTerm',
        '@id': `${url}#term`,
        name: 'Adaptive pacing',
        alternateName: 'Adaptively paced timelapse',
        description: DEFINITION,
        inDefinedTermSet: {
          '@type': 'DefinedTermSet',
          name: 'Timelapse terms',
          url: 'https://tweediastudio.com/guides',
        },
        url,
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function AdaptivePacingPage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonicalPath: CANONICAL_PATH,
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const setSectionRef = useSectionViewTracking(SECTION_IDS);

  const handleStudioCta = () => {
    trackEvent('cta_click', { cta_id: 'adaptive_pacing_try_studio', signed_in: signedIn });
    if (!signedIn) signIn();
  };

  const trackNav = (target) => () => {
    trackEvent('nav_click', { target });
  };

  const studioCta = signedIn ? (
    <Link
      to="/studio"
      className="ap-primary-cta"
      data-testid="adaptive-pacing-studio-cta"
      onClick={handleStudioCta}
    >
      Try it on your own footage
    </Link>
  ) : (
    <button
      type="button"
      className="ap-primary-cta"
      data-testid="adaptive-pacing-studio-cta"
      onClick={handleStudioCta}
    >
      Try it on your own footage
    </button>
  );

  return (
    <div className="adaptive-pacing-page">
      <Header user={user} signIn={signIn} />
      <main className="ap-content" data-testid="adaptive-pacing-content">
        <p className="ap-eyebrow">Guide</p>
        <h1>What Is Adaptive Pacing?</h1>
        <p className="ap-meta">
          The plain-English definition, and how it differs from a normal timelapse. 6 min read.
        </p>

        {/* The definition, above everything else. This block is what a search
            snippet and an answer engine quote, so it is short, complete on its
            own, and repeated nowhere in a different form. */}
        <div className="ap-definition" data-testid="adaptive-pacing-definition">
          <p className="ap-definition-label">Definition</p>
          <p className="ap-definition-text">{DEFINITION}</p>
          <p className="ap-definition-note">
            A video made this way is called <em>an adaptively paced timelapse</em>.
          </p>
        </div>

        <p className="ap-lede">
          A normal timelapse runs at one speed the whole way through, which means the hour your
          crew poured the slab gets exactly as much of your video as the hour it rained and
          nobody showed up. Adaptive pacing fixes that by letting the footage set the speed.
          This page explains what that means, how it differs from the other ways of speeding up
          a video, and when it is worth using.
        </p>

        <nav className="ap-toc" aria-label="Table of contents">
          <p className="ap-toc-title">Contents</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section
          id="pacing_definition"
          data-section-id="pacing_definition"
          ref={setSectionRef('pacing_definition')}
        >
          <h2>The short answer</h2>
          <p>
            Think of the speed of a timelapse as a dial that, on a normal one, is glued in place.
            Adaptive pacing unsticks it and lets the footage turn it: down towards real time
            whenever something is happening, up towards fast-forward whenever nothing is. The
            changes are gradual, so on screen it reads as the video breathing rather than as a
            cut.
          </p>
          <p>
            Two things it is <strong>not</strong>. It is not editing — you do not mark the good
            bits, and there is no timeline to drag. And it is not trimming: nothing is cut out,
            so the rained-out week is still in the video, it just goes by in a couple of seconds
            instead of a couple of minutes.
          </p>
        </section>

        <section
          id="pacing_problem"
          data-section-id="pacing_problem"
          ref={setSectionRef('pacing_problem')}
        >
          <h2>The problem it solves</h2>
          <p>
            Put a camera on a jobsite for four months and you come home with something close to
            100,000 frames. Almost all of them are of nothing: nights, weekends, weather delays,
            the two weeks waiting on an inspection. Speed the whole thing up enough to fit in two
            minutes and the good parts — the pour, the framing, the crane picks — flash past in
            half a second each. Slow it down enough to see them and you have a forty-minute video
            nobody will finish.
          </p>
          <p>
            That is the trade every long timelapse forces, and one fixed speed cannot win it,
            because the shoot is not evenly interesting. Adaptive pacing stops treating it as
            though it were. The runtime you were always going to spend gets spent where the work
            is.
          </p>
        </section>

        <section
          id="pacing_compare"
          data-section-id="pacing_compare"
          ref={setSectionRef('pacing_compare')}
        >
          <h2>Adaptive pacing vs the alternatives</h2>
          <p>
            Four ways to make a long shoot short, side by side. The difference that matters is
            the second column — what decides the speed — because everything else follows from it.
          </p>

          {/* Shared with the four cluster guides. Wide and scrollable on a
              desktop, one card per technique on a phone — see
              GuideComparisonTable.js for why that had to stop being five
              hand-written copies of the same markup. */}
          <GuideComparisonTable
            columns={COMPARISON_COLUMNS}
            rows={COMPARISON}
            ariaLabel="Comparison of timelapse pacing techniques"
            testId="adaptive-pacing-comparison"
          />

          {COMPARISON_NOTES.map((note) => (
            <div className="ap-note" key={note.id} data-testid={`adaptive-pacing-note-${note.id}`}>
              <h3>{note.title}</h3>
              <p>{note.body}</p>
            </div>
          ))}
        </section>

        <section id="pacing_how" data-section-id="pacing_how" ref={setSectionRef('pacing_how')}>
          <h2>How the speed gets decided</h2>
          <p>
            Nothing exotic happens, and it is worth knowing so you can tell a real implementation
            from a marketing word. Three steps:
          </p>
          <ol className="ap-steps">
            <li>
              <strong>Measure the movement.</strong> Compare each frame with the one before it
              and score how much of the picture actually changed. A crew working scores high; an
              empty site at 2am scores close to zero.
            </li>
            <li>
              <strong>Smooth the scores.</strong> Raw frame-to-frame numbers are jumpy — a truck
              crossing the shot, a cloud shadow, a bird. Smoothing them stops the speed from
              lurching around on things nobody cares about.
            </li>
            <li>
              <strong>Turn scores into speeds.</strong> High-movement moments play at the base
              speed. Quiet ones ramp up towards a ceiling you choose, then ease back down as soon
              as something starts happening again.
            </li>
          </ol>
          <p>
            The ceiling is the only number you really need an opinion about. In Tweedia Studio it
            runs from {MAX_SPEED_RANGE_LABEL} the base speed: near the bottom the effect is a
            gentle drift you barely notice, near the top the still hours genuinely fly. Wherever
            you set it, the busy stretches settle back to the base speed — the ceiling only ever
            governs how fast the quiet gets.
          </p>
        </section>

        <section
          id="pacing_motionpace"
          data-section-id="pacing_motionpace"
          ref={setSectionRef('pacing_motionpace')}
        >
          <h2>MotionPace: adaptive pacing, done for you</h2>
          <p>
            Everything above describes a technique, and you can approximate it by hand in any
            video editor. <strong>MotionPace</strong> is Tweedia Studio's automatic
            implementation: you upload the shoot, turn it on, and the three steps happen for you. There
            are no keyframes to place and no script to write, and you never have to watch the raw
            footage to find out where the interesting parts were.
          </p>
          <p>
            Two controls shape the result — a <strong>Max speed</strong> ceiling and a{' '}
            <strong>Smoothness</strong> setting that decides how gradually a speed change
            arrives. Both are free to change as often as you like: the preview updates before you
            render, so you can try a setting without spending anything.
          </p>
          <div className="ap-callout">
            <h3>See it on real footage</h3>
            <p>
              The MotionPace page charts an actual deck build — days of a fixed camera, paced
              automatically, with nothing cut out — against the same footage at a constant speed.
            </p>
            <Link
              to="/motionpace"
              className="ap-callout-cta"
              data-testid="adaptive-pacing-motionpace-link"
              onClick={trackNav('motionpace_from_adaptive_pacing')}
            >
              How MotionPace works →
            </Link>
          </div>
        </section>

        <section id="pacing_when" data-section-id="pacing_when" ref={setSectionRef('pacing_when')}>
          <h2>When it helps — and when it does not</h2>
          <p>
            Adaptive pacing earns its keep whenever the interesting part is a small fraction of
            the recording. That is most long timelapses, but not all of them.
          </p>
          <div className="ap-fit">
            <div className="ap-fit-col ap-fit-yes" data-testid="adaptive-pacing-fit-yes">
              <h3>Worth it</h3>
              <ul>
                {WHEN_IT_HELPS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="ap-fit-col ap-fit-no" data-testid="adaptive-pacing-fit-no">
              <h3>Probably not</h3>
              <ul>
                {WHEN_IT_DOES_NOT.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p>
            If you are still choosing an interval or working out how long your shoot needs to
            run, start with{' '}
            <Link to="/guides/long-term-timelapse" onClick={trackNav('guide_from_adaptive_pacing')}>
              the long-term timelapse guide
            </Link>{' '}
            or the{' '}
            <Link
              to="/tools/timelapse-calculator"
              onClick={trackNav('calculator_from_adaptive_pacing')}
            >
              timelapse calculator
            </Link>
            . Pacing is the last decision, not the first.
          </p>
        </section>

        <section id="pacing_faq" data-section-id="pacing_faq" ref={setSectionRef('pacing_faq')}>
          <h2>FAQ</h2>
          <div className="ap-faq" data-testid="adaptive-pacing-faq">
            {FAQ.map((item) => (
              <div className="ap-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="ap-final-cta" data-testid="adaptive-pacing-final-cta">
          <h2>Let the footage set the pace</h2>
          <p>
            Upload a shoot you have already filmed, turn MotionPace on, and watch a paced draft
            before you spend a credit.
          </p>
          <div className="ap-final-cta-row">
            {studioCta}
            <Link
              to="/motionpace"
              className="ap-secondary-cta"
              data-testid="adaptive-pacing-final-motionpace-link"
              onClick={trackNav('motionpace_from_adaptive_pacing_cta')}
            >
              See MotionPace
            </Link>
          </div>
          <p className="ap-note-fine">
            Tuning and previewing are always free. Credits are charged on a finished render.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default AdaptivePacingPage;
export {
  DEFINITION,
  FAQ,
  TOC,
  COMPARISON,
  COMPARISON_COLUMNS,
  COMPARISON_NOTES,
  MAX_SPEED_RANGE,
  CANONICAL_PATH,
  PAGE_TITLE,
  buildJsonLd,
};
